import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiRotateCcw, FiZoomIn, FiZoomOut } = FiIcons;

const MobileChessBoard = ({ board, selectedSquare, validMoves, onSquareClick, currentPlayer }) => {
  const [boardRotated, setBoardRotated] = useState(false);
  const [boardScale, setBoardScale] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const boardRef = useRef(null);
  const containerRef = useRef(null);

  // Touch handling for pan and zoom
  const [lastTouchDistance, setLastTouchDistance] = useState(null);
  const [initialPinchScale, setInitialPinchScale] = useState(1);

  const getPieceSymbol = (piece) => {
    if (!piece) return '';
    const symbols = {
      white: {
        king: '♔', queen: '♕', rook: '♖',
        bishop: '♗', knight: '♘', pawn: '♙'
      },
      black: {
        king: '♚', queen: '♛', rook: '♜',
        bishop: '♝', knight: '♞', pawn: '♟'
      }
    };
    return symbols[piece.color][piece.type] || '';
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      // Pinch gesture
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      setLastTouchDistance(distance);
      setInitialPinchScale(boardScale);
    } else if (e.touches.length === 1) {
      // Pan gesture
      setIsPanning(true);
    }
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    
    if (e.touches.length === 2) {
      // Handle pinch zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      
      if (lastTouchDistance) {
        const scaleChange = distance / lastTouchDistance;
        const newScale = Math.max(0.5, Math.min(2, initialPinchScale * scaleChange));
        setBoardScale(newScale);
      }
    }
  };

  const handleTouchEnd = () => {
    setIsPanning(false);
    setLastTouchDistance(null);
  };

  const zoomIn = () => {
    setBoardScale(prev => Math.min(2, prev + 0.2));
  };

  const zoomOut = () => {
    setBoardScale(prev => Math.max(0.5, prev - 0.2));
  };

  const resetView = () => {
    setBoardScale(1);
    setPanOffset({ x: 0, y: 0 });
  };

  const getSquareSize = () => {
    const screenWidth = window.innerWidth;
    const padding = 32; // 16px on each side
    const availableWidth = screenWidth - padding;
    return Math.min(availableWidth / 8, 48); // Max 48px per square
  };

  const squareSize = getSquareSize();

  return (
    <div className="flex flex-col items-center space-y-4">
      {/* Mobile Controls */}
      <div className="flex items-center gap-2 bg-black/20 backdrop-blur-sm rounded-xl p-2">
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => setBoardRotated(!boardRotated)}
          className="p-2 bg-white/10 rounded-lg text-white"
        >
          <SafeIcon icon={FiRotateCcw} />
        </motion.button>
        
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={zoomOut}
          className="p-2 bg-white/10 rounded-lg text-white"
        >
          <SafeIcon icon={FiZoomOut} />
        </motion.button>
        
        <span className="px-3 py-1 bg-white/10 rounded text-white text-sm">
          {Math.round(boardScale * 100)}%
        </span>
        
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={zoomIn}
          className="p-2 bg-white/10 rounded-lg text-white"
        >
          <SafeIcon icon={FiZoomIn} />
        </motion.button>
        
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={resetView}
          className="px-3 py-2 bg-blue-600 rounded-lg text-white text-sm font-medium"
        >
          Reset
        </motion.button>
      </div>

      {/* Board Container */}
      <div 
        ref={containerRef}
        className="relative overflow-hidden rounded-2xl"
        style={{ 
          width: squareSize * 8 + 32,
          height: squareSize * 8 + 32,
          maxWidth: '95vw',
          maxHeight: '60vh'
        }}
      >
        <motion.div
          ref={boardRef}
          className="bg-gradient-to-br from-amber-100 to-amber-200 p-4 rounded-2xl shadow-2xl border-4 border-amber-800"
          style={{
            transform: `scale(${boardScale}) translate(${panOffset.x}px, ${panOffset.y}px) ${boardRotated ? 'rotate(180deg)' : ''}`,
            transformOrigin: 'center center'
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Chess Board Grid */}
          <div 
            className="grid grid-cols-8 gap-0 bg-amber-900 p-2 rounded-xl touch-manipulation"
            style={{ 
              width: squareSize * 8 + 16,
              height: squareSize * 8 + 16
            }}
          >
            {board.map((row, rowIndex) =>
              row.map((piece, colIndex) => {
                const displayRow = boardRotated ? 7 - rowIndex : rowIndex;
                const displayCol = boardRotated ? 7 - colIndex : colIndex;
                
                const isSelected = selectedSquare && 
                  selectedSquare[0] === rowIndex && selectedSquare[1] === colIndex;
                const isValidMove = validMoves.some(move => 
                  move.row === rowIndex && move.col === colIndex);
                const isLight = (rowIndex + colIndex) % 2 === 0;

                return (
                  <motion.div
                    key={`${rowIndex}-${colIndex}`}
                    className={`
                      flex items-center justify-center cursor-pointer relative
                      ${isLight ? 'bg-amber-50' : 'bg-amber-600'}
                      ${isSelected ? 'ring-4 ring-blue-400 ring-opacity-75' : ''}
                      ${isValidMove ? 'ring-2 ring-green-400' : ''}
                    `}
                    style={{ 
                      width: squareSize,
                      height: squareSize,
                      minHeight: '40px',
                      minWidth: '40px'
                    }}
                    onClick={() => onSquareClick(rowIndex, colIndex)}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: (rowIndex * 8 + colIndex) * 0.01 }}
                  >
                    {/* Square coordinates */}
                    {colIndex === 0 && (
                      <span className="absolute -left-6 text-amber-800 font-semibold text-xs">
                        {boardRotated ? rowIndex + 1 : 8 - rowIndex}
                      </span>
                    )}
                    
                    {rowIndex === 7 && (
                      <span className="absolute -bottom-6 text-amber-800 font-semibold text-xs">
                        {String.fromCharCode(97 + (boardRotated ? 7 - colIndex : colIndex))}
                      </span>
                    )}

                    {/* Valid move indicators */}
                    {isValidMove && !piece && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="w-3 h-3 bg-green-400 rounded-full opacity-60"
                      />
                    )}

                    {isValidMove && piece && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute inset-0 border-2 border-red-400 rounded-full opacity-60"
                      />
                    )}

                    {/* Chess piece */}
                    {piece && (
                      <motion.span
                        className="select-none"
                        style={{ 
                          fontSize: `${Math.max(squareSize * 0.6, 20)}px`,
                          transform: boardRotated && !boardRotated ? 'rotate(180deg)' : 'none'
                        }}
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: "spring", stiffness: 200, damping: 15 }}
                        whileHover={{ scale: 1.1 }}
                      >
                        {getPieceSymbol(piece)}
                      </motion.span>
                    )}
                  </motion.div>
                );
              })
            )}
          </div>

          {/* Board coordinates */}
          <div className="flex justify-between px-6 mt-2">
            {['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'].map((letter, index) => (
              <span key={letter} className="text-amber-800 font-semibold text-xs">
                {boardRotated ? String.fromCharCode(104 - index) : letter}
              </span>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Mobile Game Status */}
      <div className="w-full max-w-sm bg-black/20 backdrop-blur-sm rounded-xl p-4">
        <div className="text-center">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg mb-2 ${
            currentPlayer === 'white' 
              ? 'bg-white text-black' 
              : 'bg-gray-800 text-white border border-white/20'
          }`}>
            <span className="text-2xl">
              {currentPlayer === 'white' ? '♔' : '♛'}
            </span>
            <span className="font-semibold capitalize">
              {currentPlayer}'s Turn
            </span>
          </div>
          
          <p className="text-gray-300 text-sm">
            Tap a piece to select, then tap a highlighted square to move
          </p>
        </div>
      </div>
    </div>
  );
};

export default MobileChessBoard;